import React from 'react'
import { useRouter } from 'next/router'
import { useFormContext } from 'react-hook-form'
import { useQueryClient } from 'react-query'

import SearchBoxComponent from 'components/SearchBox'
import SearchNotice from 'app/SearchPage/components/SearchNotice'
import useSearchQuery from 'app/SearchPage/hooks/useSearchQuery'
import useSETrackEvent from 'app/SearchPage/hooks/useSETrackEvent'
import {
  useContentSearch,
  useContentSearchFilter,
  useContentSearchTracker,
} from 'stores/content-search'

const SearchBox = () => {
  const inputRef = React.useRef<HTMLInputElement>()
  const [prevSearchQuery, setPrevSearchQuery] = React.useState<string>('')
  const [showNotice, setShowNotice] = React.useState<boolean>(false)
  const [isUserAlreadyTyped, setUserAlreadyTyped] =
    React.useState<boolean>(false)
  const { hasSearchQueryFromURL, searchQueryFromURL } = useSearchQuery()
  const queryClient = useQueryClient()
  const contentSearchStore = useContentSearch()
  const { selectedFormats, selectedTopics, hasAnyFilterSelected } =
    useContentSearchFilter()
  const contentSearchTrackerStore = useContentSearchTracker()
  const { SETrackSearchPage } = useSETrackEvent()

  const router = useRouter()
  const {
    clearErrors,
    handleSubmit,
    register,
    setError,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useFormContext()
  const watchedSearchQuery = watch('searchQuery')
  const { ref, onChange, ...searchQueryProps } = register('searchQuery')

  const onSubmit = (data) => {
    const searchQuery = (data.searchQuery ?? '').trim()
    const currentErrorType = contentSearchStore.errorType
    const hasError = Boolean(currentErrorType) && !hasAnyFilterSelected()
    const fiterParams = {
      ...(selectedFormats.length ? { formats: selectedFormats } : {}),
      ...(selectedTopics.length ? { topics: selectedTopics } : {}),
    }
    const isSameKeyword = searchQuery === searchQueryFromURL

    SETrackSearchPage('ENTER_SEARCH', { text_input: data.searchQuery ?? '' })

    if (searchQuery === '') {
      SETrackSearchPage('EMPTY_INPUT_SEARCH')
      setError('searchQuery', {
        type: 'required',
        message: 'Jangan lupa ketik yang mau Anda cari...',
      })
      return
    }

    if (isInvalid) return

    const redirectParams = {
      pathname: '/pencarian',
      query: {
        q: searchQuery,
        ...(isSameKeyword ? fiterParams : {}),
      },
    }
    contentSearchTrackerStore.setEventTrigger(
      hasSearchQueryFromURL ? 'SEARCH_RESULTS_PAGE' : 'SEARCH_PAGE'
    )
    inputRef?.current?.blur?.()
    setValue('searchQuery', searchQuery)
    clearErrors('searchQuery')

    if (isSameKeyword && hasError) {
      queryClient.refetchQueries({
        queryKey: [
          'content-search-results-infinite-query',
          searchQuery.toLowerCase(),
          fiterParams,
        ],
      })
    } else {
      queryClient.invalidateQueries({
        queryKey: [
          'content-search-results-infinite-query',
          searchQuery.toLowerCase(),
        ],
      })
    }

    if (hasSearchQueryFromURL && !hasError) {
      router.push(redirectParams)
      return
    }
    router.replace(redirectParams)
  }

  const searchSubmitHandler = handleSubmit(onSubmit)

  const isInvalid = React.useMemo(
    () => Boolean(errors?.searchQuery),
    [errors?.searchQuery]
  )

  React.useEffect(() => {
    if (showNotice) return

    if (Boolean(watchedSearchQuery)) {
      setShowNotice(true)
    }
  }, [showNotice, watchedSearchQuery])

  React.useEffect(() => {
    if (hasSearchQueryFromURL) {
      setValue('searchQuery', searchQueryFromURL)
      clearErrors('searchQuery')
      setPrevSearchQuery(searchQueryFromURL)
    }
    return () => {
      setPrevSearchQuery('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSearchQueryFromURL, searchQueryFromURL])

  React.useEffect(() => {
    if (Boolean(inputRef?.current) && !hasSearchQueryFromURL) {
      inputRef.current?.focus?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputRef?.current])

  React.useEffect(() => {
    if (isUserAlreadyTyped) {
      return
    }

    if (!hasSearchQueryFromURL && isDirty && Boolean(watchedSearchQuery)) {
      setUserAlreadyTyped(true)
      SETrackSearchPage('START_INPUT_SEARCH')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, watchedSearchQuery])

  return (
    <>
      <SearchBoxComponent
        {...searchQueryProps}
        ref={(e) => {
          ref(e)
          inputRef.current = e
        }}
        data-testid="search-input-box"
        className="truncate"
        autoComplete="off"
        maxLength={255}
        showCounter={false}
        isClearable={Boolean(watchedSearchQuery)}
        placeholder={'Cari "kegiatan intrakurikuler", "aksi nyata", dll'}
        isInvalid={isInvalid}
        errorMessage={errors?.searchQuery?.message}
        onKeyDown={(e) => {
          if (e.key == 'Enter') {
            searchSubmitHandler()
          }
        }}
        onClickButton={searchSubmitHandler}
        onChange={(e: any) => {
          // clicking on Clear button events currently not setting inputType
          if (!Boolean(e?.nativeEvent?.inputType) && e?.target?.value === '') {
            SETrackSearchPage('DELETE_INPUT_SEARCH', {
              text_input: prevSearchQuery,
            })
          }
          setPrevSearchQuery(watchedSearchQuery)
          onChange(e)
        }}
      />

      {!hasSearchQueryFromURL && showNotice && (
        <div className="mt-4">
          <SearchNotice />
        </div>
      )}
    </>
  )
}

export default SearchBox
